(() => {
  if (customElements.get('product-gallery')) return;

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('.product-gallery__slide[data-media-id]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));
      this.controls = this.querySelector('[data-gallery-controls]');
      this.currentLabel = this.querySelector('[data-gallery-current]');
      this.previousButton = this.querySelector('[data-gallery-previous]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.dialogImage = this.querySelector('[data-gallery-dialog-image]');
      this.status = this.querySelector('[data-gallery-status]');
      this.retryButton = this.querySelector('[data-gallery-retry]');
      this.activeIndex = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onTrackKeydown = this.onTrackKeydown.bind(this);
      this.onDialogKeydown = this.onDialogKeydown.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      document.addEventListener('puffle:variant-change', this.onVariantChange);
      this.retryButton?.addEventListener('click', () => {
        if (this.requestedVariantId) this.loadVariantGallery(this.requestedVariantId);
      });

      if (!this.track || this.slides.length === 0) return;

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onTrackKeydown);

      this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));

      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => this.selectMedia(thumbnail.dataset.mediaId));
      });

      this.hydrateSlide(this.slides[0]);
      if ('IntersectionObserver' in window) {
        this.deferredObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.hydrateSlide(entry.target);
          });
        }, { root: this.track, threshold: 0.01 });
        this.slides.slice(1).forEach((slide) => this.deferredObserver.observe(slide));
      } else {
        this.slides.forEach((slide) => this.hydrateSlide(slide));
      }

      this.querySelectorAll('[data-gallery-zoom]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => this.openZoom(event, trigger));
      });

      this.querySelector('[data-gallery-dialog-close]')?.addEventListener('click', () => this.closeZoom());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeZoom();
      });
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeZoom();
      });
      this.dialog?.addEventListener('keydown', this.onDialogKeydown);
      this.dialog?.addEventListener('close', this.onDialogClose);

      this.updateActive(0, false);
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('keydown', this.onTrackKeydown);
      this.dialog?.removeEventListener('keydown', this.onDialogKeydown);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      document.removeEventListener('puffle:variant-change', this.onVariantChange);
      this.deferredObserver?.disconnect();
      this.galleryRequest?.abort();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
    }

    async onVariantChange(event) {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      const variantId = event.detail?.variant?.id;
      if (!variantId) return;
      this.requestedVariantId = String(variantId);
      this.galleryRequest?.abort();
      if (this.requestedVariantId === this.dataset.variantId) {
        this.removeAttribute('aria-busy');
        this.status?.setAttribute('hidden', '');
        return;
      }
      await this.loadVariantGallery(variantId);
    }

    async loadVariantGallery(variantId) {
      this.galleryRequest?.abort();
      this.galleryRequest = new AbortController();
      const requestedVariantId = String(variantId);
      this.requestedVariantId = requestedVariantId;
      this.setAttribute('aria-busy', 'true');
      this.status?.setAttribute('hidden', '');

      try {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: this.galleryRequest.signal
        });
        if (!response.ok) throw new Error(`Gallery request failed (${response.status})`);

        const markup = await response.text();
        const rendered = new DOMParser().parseFromString(markup, 'text/html');
        const nextGallery = rendered.querySelector(`product-gallery[data-section-id="${CSS.escape(this.dataset.sectionId)}"]`);
        if (!nextGallery) throw new Error('The selected variant gallery was not returned.');

        const selectedVariantId = this.closest('puffle-product')?.querySelector('[data-variant-id]')?.value;
        if (this.requestedVariantId !== requestedVariantId || String(selectedVariantId) !== requestedVariantId) return;

        this.replaceWith(nextGallery);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          this.removeAttribute('aria-busy');
          this.status?.removeAttribute('hidden');
          console.warn('PUFFLE: variant gallery could not be updated.', error);
        }
      }
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const trackRect = this.track.getBoundingClientRect();
        let closestIndex = this.activeIndex;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== this.activeIndex) this.updateActive(closestIndex);
      });
    }

    onTrackKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1);
      }
    }

    onDialogKeydown(event) {
      if (event.key !== 'Escape' || !this.dialog?.open) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeZoom();
    }

    onDialogClose() {
      document.documentElement.classList.remove('modal-open');
      document.body.classList.remove('modal-open');
      const trigger = this.zoomTrigger;
      this.zoomTrigger = null;
      requestAnimationFrame(() => {
        if (!trigger?.isConnected || trigger.getClientRects().length === 0) return;
        trigger.focus({ preventScroll: true });
      });
    }

    selectMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    goTo(index) {
      if (!this.slides.length) return;
      const normalizedIndex = (index + this.slides.length) % this.slides.length;
      const targetSlide = this.slides[normalizedIndex];
      const pageX = window.scrollX;
      const pageY = window.scrollY;

      this.hydrateSlide(targetSlide);

      // Keep desktop thumbnail/arrow navigation intentionally immediate. A smooth
      // scroll inside a snap track can make Chromium scroll the page vertically
      // while it resolves the new snap target, even though only the gallery should
      // move. Native touch swiping remains unchanged on mobile.
      if (targetSlide && this.track) this.track.scrollLeft = targetSlide.offsetLeft;
      this.updateActive(normalizedIndex);

      const restorePagePosition = () => {
        if (Math.abs(window.scrollX - pageX) > 1 || Math.abs(window.scrollY - pageY) > 1) {
          window.scrollTo({ left: pageX, top: pageY, behavior: 'auto' });
        }
      };
      restorePagePosition();
      requestAnimationFrame(restorePagePosition);
    }

    updateActive(index, pauseMedia = true) {
      if (!this.slides.length) return;
      this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const activeSlide = this.slides[this.activeIndex];

      this.hydrateSlide(activeSlide);

      this.slides.forEach((slide) => {
        const isActive = slide === activeSlide;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
        slide.querySelectorAll('[data-gallery-focusable]').forEach((control) => {
          if (isActive) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        });
        if (pauseMedia && !isActive) this.pauseMedia(slide);
      });

      this.thumbnails.forEach((thumbnail) => {
        const isActive = thumbnail.dataset.mediaId === activeSlide?.dataset.mediaId;
        thumbnail.classList.toggle('is-active', isActive);
        if (isActive) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });

      if (this.currentLabel) this.currentLabel.textContent = String(this.activeIndex + 1).padStart(2, '0');
    }

    hydrateSlide(slide) {
      if (!slide) return;
      slide.querySelectorAll('[data-gallery-deferred-image]').forEach((image) => {
        const sourceSet = image.getAttribute('data-srcset');
        const source = image.getAttribute('data-src');
        if (sourceSet) image.setAttribute('srcset', sourceSet);
        if (source) image.setAttribute('src', source);
        image.removeAttribute('data-srcset');
        image.removeAttribute('data-src');
        image.removeAttribute('data-gallery-deferred-image');
      });
      this.deferredObserver?.unobserve(slide);
    }

    pauseMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
    }

    openZoom(event, trigger) {
      if (!this.dialog?.showModal || !this.dialogImage || this.dialog.open) return;
      event.preventDefault();
      this.zoomTrigger = trigger;
      this.dialogImage.src = trigger.dataset.zoomSrc || trigger.href;
      this.dialogImage.alt = trigger.dataset.zoomAlt || '';
      document.documentElement.classList.add('modal-open');
      document.body.classList.add('modal-open');
      try {
        this.dialog.showModal();
      } catch (error) {
        document.documentElement.classList.remove('modal-open');
        document.body.classList.remove('modal-open');
        this.zoomTrigger = null;
        console.warn('PUFFLE: product image zoom could not be opened.', error);
        return;
      }
      this.querySelector('[data-gallery-dialog-close]')?.focus();
    }

    closeZoom() {
      if (this.dialog?.open) this.dialog.close();
    }
  }

  customElements.define('product-gallery', ProductGallery);
})();
